import React, { useEffect, useRef, useState } from 'react'
import { CheqLoader, Stepper } from '@clickcease/cc-client-components-lib'
import { useGetDomains } from 'Features/domainsManagement/api/getDomains'
import { useAccountStatus } from 'Features/MainWrapper/api/getAccountStatus'
import { useNavigate } from 'react-router-dom'
import { FlexCentered } from './Onboarding.styled'
import { FirstDomain } from './FirstDomain/FirstDomain'
import { ImplementTag } from './ImplementTag/ImplementTag'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { useGetPackagingLimits } from 'Features/accountSettings/api/getPackagingLimits'
import { useIsFirstLogin } from 'Hooks/useIsFirstLogin'
import { useSplit } from 'Hooks/useSplit'
import useMainWrapperStore, { TRIGGER_OPTIONS } from 'Features/MainWrapper/stores/mainWrapper'

export const Onboarding = () => {
  const navigate = useNavigate()
  const { isPaidMarketingUsed } = useIsFirstLogin()
  const { setTriggerLocation } = useMainWrapperStore()
  const { refetch: checkAccountStatus, data: accountIsActive } = useAccountStatus()
  const { data: limitsData } = useGetPackagingLimits()
  const [currentStep, setCurrentStep] = useState<number>(1)
  const addedDomainId = useRef<undefined | number>(undefined)
  const addedDomainAddress = useRef<undefined | string>(undefined)
  const { setShowCheckoutSuccess } = useAppStore()
  const splitWelcomingExperience = useSplit('CE_Welcoming_Experience')

  const { data, isLoading } = useGetDomains()
  const domains = data?.data.domains
  const domainsExists = domains && domains.length > 0
  const addedDomain = domains && domains.length > 0 ? domains[0] : undefined
  const existingDomainAddress = addedDomainAddress.current
    ? addedDomainAddress.current
    : addedDomain?.address || ''

  const finishOnboarding = async () => {
    if (!accountIsActive) await checkAccountStatus()
    navigate('/')
  }

  useEffect(() => {
    setShowCheckoutSuccess(false)
    setTriggerLocation(TRIGGER_OPTIONS.ONBOARDING)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (domainsExists && !addedDomainId.current && currentStep !== 2) {
      setCurrentStep(2)
    }
    if (accountIsActive) {
      void finishOnboarding()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainsExists, accountIsActive])

  const onDomainAdded = (domainId: number, domainAddress: string, isNoTimeout?: boolean) => {
    addedDomainId.current = domainId
    addedDomainAddress.current = domainAddress
    // we add timeout to wait till wirdpress check is finished
    setTimeout(
      () => {
        setCurrentStep(2)
      },
      isNoTimeout ? 0 : 3500
    )
  }

  const onBackToFirstStep = () => setCurrentStep(1)

  const onFinish = () => {
    // If first time user Added domain and then clicked on finish && has Paid Marketing - redirect to PM
    if (limitsData?.paidMarketingAllowed && !isPaidMarketingUsed && splitWelcomingExperience) {
      sessionStorage.setItem('showPaidMarketingUsedTutorial', 'true')
      navigate('/paid-marketing/dashboard')
    } else navigate('/')
  }

  const onSetStep = (step: number) => {
    if (step === 1 && currentStep === 2 && domains?.length === 1) {
      onBackToFirstStep()
    }
    if (
      currentStep === 1 &&
      step === 2 &&
      (addedDomainId.current || (domains && domains?.length > 0))
    ) {
      setCurrentStep(2)
    }
  }

  return (
    <FlexCentered id={'onboarding-page'}>
      {isLoading ? (
        <CheqLoader />
      ) : (
        <>
          <Stepper
            currentStep={currentStep}
            steps={2}
            onChangeStep={onSetStep}
            size="large"
            containerCss={{ width: '155px', marginBottom: '$8' }}
          />
          {currentStep === 1 ? (
            <FirstDomain
              onAddDomain={onDomainAdded}
              addedDomainAddress={existingDomainAddress || ''}
              addedDomainId={addedDomainId.current}
            />
          ) : (
            <ImplementTag
              onBack={onBackToFirstStep}
              onFinish={onFinish}
              addedDomainAddress={existingDomainAddress || ''}
            />
          )}
        </>
      )}
    </FlexCentered>
  )
}
